import type { ScenarioPreviewOut } from "../lib/api";

export function ScenarioSamplingCoverage({ report }: { report: ScenarioPreviewOut }) {
  const sample = report.sampling;
  if (!sample) return <p className="text-xs text-muted-foreground">
    Legacy seven-day full analysis · This saved run predates representative sampling.
  </p>;
  const missingAgents = sample.agents.filter((x) => !x.sampled).length;
  return <section aria-label="Sampling coverage" className="rounded-md border border-border p-4 text-sm space-y-3">
    <h3 className="font-semibold">Sampling coverage</h3>
    <p>{sample.viable} viable calls · {sample.eligible} evidence-eligible calls · {sample.sampled} sampled calls</p>
    <p className="text-xs text-muted-foreground">
      Evidence-eligible means detected resistance with a valid stored evidence span.
      Selected spans must also reconstruct a usable customer/agent exchange.
      AI findings use the saved sample, not every call. Population resistance counts below
      cover all {sample.resistance_detected} calls with detected resistance in this period.
      Sample proportions are not population conversion rates.
    </p>
    <table className="w-full text-left text-xs">
      <thead><tr><th>NZ date section (inclusive)</th><th>Eligible</th><th>Sampled</th></tr></thead>
      <tbody>{sample.sections.map((s) => <tr key={s.start}>
        <td>{s.start} – {s.end}{!s.eligible ? " · No eligible evidence" : !s.sampled ? " · Not represented" : ""}</td>
        <td>{s.eligible}</td><td>{s.sampled}</td>
      </tr>)}</tbody>
    </table>
    <p className="text-xs">{sample.agents.length - missingAgents} of {sample.agents.length} eligible agents represented
      {missingAgents ? ` · ${missingAgents} eligible agents not represented` : ""}.</p>
    <ul className="text-xs">{sample.outcomes.map((x) => <li key={x.group}>
      {report.outcome_label || "Success measure"} {x.group === "True" ? "detected" : "not detected"}:
      {" "}{x.eligible} eligible / {x.sampled} sampled{!x.eligible ? " · Unavailable" : !x.sampled ? " · Not represented" : ""}
    </li>)}</ul>
    <details><summary>Resistance coverage and exclusions</summary>
      <ul className="mt-2 text-xs">{sample.resistance.map((x) => <li key={x.group}>
        {x.group}: {x.eligible} eligible / {x.sampled} sampled
        {!x.eligible ? " · Unavailable" : !x.sampled ? " · Not represented" : ""}
      </li>)}</ul>
      <ul className="mt-2 text-xs">{Object.entries(sample.exclusions).map(([key, count]) =>
        <li key={key}>{key.replaceAll("_", " ")}: {count}</li>)}</ul>
    </details>
    <p className="text-xs text-muted-foreground">
      Each analysis uses up to {sample.max_calls} calls. Some groups may have eligible calls and still have none in the sample.
    </p>
  </section>;
}
